import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { showSuccessNotification } from "../../../../../components/NotificationAlerts";
import { ResourceName } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH } from "../../../../../core/router/types/routerTypes";
import { useAcquirerLoadQuery, useAcquirerUpdateMutation } from "../../services/server/AcquirerApi";
import type { AcquirerEntity } from "../../types/AcquirerEntity";

export const VirtualAcquirerEditForm: FC<FormProps> = ({ runSubmit }) => {
    const navigate = useNavigate();
    const { id } = useParams();

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("acquirer-virtual");

    const [formData, setFormData] = useState<AcquirerEntity>({} as AcquirerEntity);
    const { data: loadFormData, isFetching: loadFormIsInProgress } = useAcquirerLoadQuery({ entityId: id });
    useEffect(() => {
        if (loadFormData) {
            // List of fields to check and clear if missing
            const specialFields: (keyof AcquirerEntity)[] = ["intraEEAAcquirer", "outEEAAcquirer", "nationalDefaultAcquirer"];
            // Copy the loaded data
            const newFormData = { ...loadFormData };
            // For each special field, if it's missing in the backend response, clear it
            specialFields.forEach((field) => {
                if (!(field in loadFormData)) {
                    newFormData[field] = undefined;
                }
            });
            setFormData(newFormData);
        }
    }, [loadFormData, setFormData]);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useAcquirerUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "Acquirer",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(`${BASE_PATH}/${ResourceName.ACQUIRER}`);
            }
        },
        [saveFormData, formData, navigate, dispatch],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<AcquirerEntity>
                    name="AcquirerVirtualEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
